import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, LockKeyhole } from "lucide-react";

import { getHttpStatus } from "@/core/api/api-error-handler";
import {
  BackofficeAccessDeniedError,
  loginWithPassword,
} from "@/core/auth/password-grant";
import { Alert, AlertDescription } from "@/core/components/ui/alert";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import {
  createKeycloakLoginSchema,
  type KeycloakLoginFormValues,
} from "@/features/auth/schemas/keycloak-login.schema";

/**
 * Formulaire de connexion (mode password, guide API v1.3) : l'utilisateur ne
 * renseigne que username + password, envoyés en JSON au endpoint PUBLIC du
 * user-service (`/gw/user/api/v1/Auth/login`) qui relaie à Keycloak. Succès →
 * session synchronisée dans le session store puis redirection /app.
 */
export function LoginForm() {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue (schéma zod traduit reconstruit au changement de langue).
  const schema = useMemo(() => createKeycloakLoginSchema(), [i18n.language]);

  const form = useForm({
    defaultValues: { username: "", password: "" } as KeycloakLoginFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setSubmitError(null);
      try {
        await loginWithPassword(parsed.data);
        // RequireAuth redirigera lui-même sur `/verify-otp` tant que l'OTP
        // post-login n'est pas validé — on vise `/app` et le gate joue.
        navigate("/app", { replace: true });
      } catch (error) {
        // Compte sans rôle back-office : rejeté après un login pourtant
        // valide (session déjà révoquée côté client par password-grant).
        if (error instanceof BackofficeAccessDeniedError) {
          setSubmitError(t("auth.login.error.noBackofficeAccess"));
          return;
        }
        // Guide v1.3 : 401 identifiants invalides / brute-force lock, 422
        // email non vérifié ou compte désactivé, 503 Keycloak injoignable.
        const status = getHttpStatus(error);
        setSubmitError(
          status === 401
            ? t("auth.login.error.invalidCredentials")
            : status === 422
              ? t("auth.login.error.unverifiedOrDisabled")
              : status === 503
                ? t("auth.login.error.serviceUnavailable")
                : t("error")
        );
      }
    },
  });

  const fieldError =
    (name: keyof KeycloakLoginFormValues) =>
    ({ value }: { value: unknown }) => {
      const result = schema.shape[name].safeParse(value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <div className="flex w-full max-w-md flex-col gap-6">
      <Card className="w-full shadow-lg">
        <CardHeader className="items-center gap-3">
          <CardDescription className="flex items-center gap-1.5 text-muted-foreground">
            <img
              src="/flash_pay_logo.png"
              alt=""
              aria-hidden="true"
              className="size-4"
            />
            <LockKeyhole className="size-3.5" />
            {t("auth.login.subtitle")}
          </CardDescription>
        </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          {submitError ? (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}

          <form.Field name="username" validators={{ onChange: fieldError("username") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="username">{t("auth.login.username")}</Label>
                <Input
                  id="username"
                  autoComplete="username"
                  autoFocus
                  placeholder={t("auth.login.usernamePlaceholder")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="password" validators={{ onChange: fieldError("password") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="password">{t("auth.login.password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    className="pr-10"
                    placeholder={t("auth.login.passwordPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 size-8 -translate-y-1/2"
                    aria-label={t("auth.login.togglePassword")}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </Button>
                </div>
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-accent-dark underline-offset-4 hover:underline"
                  >
                    {t("auth.login.forgotPassword")}
                  </Link>
                </div>
              </div>
            )}
          </form.Field>

          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? <ButtonSpinner /> : null}
                {t("auth.login.submit")}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </CardContent>
      </Card>
    </div>
  );
}
